import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { ScheduledActionDto, ResponseDto } from "@mimber/schema"
import { announcementsKeys } from "../announcements.utils"

function isNotFoundError(
  error: unknown
): error is { response: { status: number } } {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { status?: number } }).response ===
      "object" &&
    (error as { response: { status?: number } }).response?.status === 404
  )
}

export const useAnnouncementSchedule = (
  uuid: string,
  options?: UseQueryOptions<ResponseDto<ScheduledActionDto> | null, Error>
): UseQueryResult<ResponseDto<ScheduledActionDto> | null, Error> => {
  guardAppUsage("mimber-organization", "useAnnouncementSchedule")

  const { server } = useServerContext()

  return useQuery({
    queryKey: announcementsKeys.getSchedule(uuid),
    queryFn: async () => {
      try {
        const response = await server.get<ResponseDto<ScheduledActionDto>>(
          `announcements/${uuid}/schedule`
        )
        return response.data
      } catch (error) {
        if (isNotFoundError(error)) {
          return null
        }
        throw error
      }
    },
    staleTime: 1 * 60 * 1000,
    enabled: !!uuid,
    ...options,
  })
}
